// The 99% confidence ellipse around a lightning strike.
//
// The map's endpoint (/lassie/historic-lightning-strikes) does not send this: 36 coordinate
// pairs per strike are several times the size of the strike itself. Every value it is derived
// from already travels with the strike, so it is built here instead, and only for the strikes
// the map actually draws.
//
// Endpoints that still send a confidence_ellipse keep working - OlMap uses theirs when present.

// sqrt(chi2.ppf(0.99, df=2) / chi2.ppf(0.5, df=2)) - scales the reported 50% axes up to 99%.
const CONFIDENCE_SCALE = 2.577567882670546;
const POINTS = 36;
const EARTH_RADIUS_KM = 6371;
const DEG = Math.PI / 180;

/**
 * @returns {Array<[number, number]>} closed ring of [lon, lat] pairs
 */
export default function confidenceEllipse({
  lat,
  lon,
  semi_major_axis,
  semi_minor_axis,
  ellipse_angle,
}) {
  const semiMajor = semi_major_axis * CONFIDENCE_SCALE;
  const semiMinor = semi_minor_axis * CONFIDENCE_SCALE;

  const bearing = (90 - ellipse_angle) * DEG;
  const cosBearing = Math.cos(bearing);
  const sinBearing = Math.sin(bearing);

  // The ellipse is measured in km, so latitude converts directly while longitude has to be
  // stretched by the cosine of the latitude we are at.
  const latPerKm = 1 / (EARTH_RADIUS_KM * DEG);
  const lonPerKm = latPerKm / Math.cos(lat * DEG);

  const ring = new Array(POINTS);
  for (let i = 0; i < POINTS; i++) {
    // 0..360 inclusive, so the last point closes the ring onto the first
    const angle = ((i * 360) / (POINTS - 1)) * DEG;
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);

    const x = semiMajor * cosAngle * cosBearing - semiMinor * sinAngle * sinBearing;
    const y = semiMajor * cosAngle * sinBearing + semiMinor * sinAngle * cosBearing;

    ring[i] = [lon + x * lonPerKm, lat + y * latPerKm];
  }

  return ring;
}
